'use client'
import classNames from 'classnames'
import { FC, PropsWithChildren, useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { useQuinceRouter } from '@src/modules/navigation'
import { useStatsigNewCLPFilterExperiment } from '@src/pages/[...slug]/components/collection/hooks/useStatsigNewCLPFilterExperiment'
import { FilterSection, FilterSectionProps, LoaderWrapper } from '@src/quince-components/src'
import {
  FilterSectionWithDrawer,
  FilterWithSidebar,
  FilterWithSidebarItemCountAndSort,
  MobileFilters,
} from '@src/quince-components/src/atoms/filterSection/menu'
import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'
import SomethingWentWrong from '@src/quince-site-common/src/organisms/somethingWentWrong/somethingWentWrong'

import { useSearchResults } from './hooks/useSearchResults'
import { SearchResults } from './searchResults/SearchResults'
import styles from './searchResultsSection.module.scss'

function SearchResultsSection() {
  const {
    productCards,
    isLayoutLoading,
    isError,
    searchQuery,
    intersectRef,
    isMoreProductsLoading,
    totalProducts,
    filters,
    selectedFilters,
    updateQuery,
  } = useSearchResults()

  const { t } = useTranslation('common')
  const { deviceType } = useDeviceInfo()
  const isDesktop = deviceType === DEVICE_TYPES.DESKTOP

  const {
    isNewCLPFiltersExperimentActive,
    showDesktopFilterWithSidebar,
    showMobileFilterWithDrawer,
    isNewCLPFiltersExperimentInitialized,
    showDesktopFilterWithDrawer,
  } = useStatsigNewCLPFilterExperiment()

  const handleResetFilters = useCallback(() => {
    updateQuery({ filter: { type: 'reset' } })
  }, [updateQuery])

  if (isError) {
    // eslint-disable-next-line i18next/no-literal-string
    return <SomethingWentWrong wrapperClass={styles.somethingWentWrongWrapper} minHeight={'20rem'} />
  }

  function getFilterSectionComponent(): React.ComponentType<FilterSectionProps> {
    if (isNewCLPFiltersExperimentActive) {
      if (isDesktop) {
        if (showDesktopFilterWithDrawer) {
          return FilterSectionWithDrawer
        }
        if (showDesktopFilterWithSidebar) {
          return FilterWithSidebar
        }
      } else if (showMobileFilterWithDrawer) {
        return MobileFilters
      }
    }

    return FilterSection
  }

  function renderFilterSection(Component: React.ComponentType<FilterSectionProps>) {
    const baseProps = {
      noOfItems: totalProducts,
      filters: filters,
      sortBy: '',
      selectedFilters: selectedFilters,
      updateQuery: updateQuery,
      showSelectedOption: true,
      shouldRenderColorFilterHtml: false,
      skipSorting: true,
      className: styles.searchResultsFilterSection,
    }

    // Pass showGroupedPage={false} to both FilterSectionWithDrawer and FilterWithSidebar for search pages
    if (Component === FilterSectionWithDrawer) {
      return <FilterSectionWithDrawer {...baseProps} showGroupedPage={false} />
    }

    if (Component === FilterWithSidebar) {
      return <FilterWithSidebar {...baseProps} showGroupedPage={false} />
    }

    return <Component {...baseProps} />
  }

  return (
    <div>
      {/* eslint-disable-next-line i18next/no-literal-string */}
      <LoaderWrapper showLoader={!isNewCLPFiltersExperimentInitialized} minHeight={'40rem'}>
        {isNewCLPFiltersExperimentActive && (filters.length > 0 || totalProducts) ? (
          <div
            className={`${styles.newCLPFiltersSearchTitleContainer} ${
              showDesktopFilterWithSidebar ? styles.newCLPFiltersSearchTitleContainer__withSidebar : ''
            }`}
          >
            <h1
              className={`${styles.newCLPFiltersSearchTitle} ${
                showDesktopFilterWithDrawer ? styles.newCLPFiltersGroupASearchTitle : ''
              }`}
            >{`"${searchQuery.toUpperCase()}"`}</h1>
            {(!isDesktop || showDesktopFilterWithDrawer) && (
              <p
                className={styles.newCLPFiltersProductCountText}
              >{`${t('showing')} ${t('totalitems_items', { totalItems: totalProducts })}`}</p>
            )}
          </div>
        ) : null}
        {showDesktopFilterWithDrawer && filters.length ? renderFilterSection(getFilterSectionComponent()) : null}
        <div
          id={'newSearchSection'}
          className={`${
            isDesktop && showDesktopFilterWithSidebar ? styles.searchResultsSectionNewCLPFiltersGroupB : ''
          } ${isNewCLPFiltersExperimentActive ? styles.searchResultsSectionNewCLPFilters : ''}`}
        >
          {!showDesktopFilterWithDrawer && filters.length ? renderFilterSection(getFilterSectionComponent()) : null}
          <div
            className={classNames(!totalProducts ? styles.noResultsContainer : '', {
              [`${styles.noResultsWithoutFilter}`]: !Object.keys(filters).length,
              [`${styles.noResultsContainer__newCLPFiltersDesktopGroupB}`]: showDesktopFilterWithSidebar,
            })}
          >
            {showDesktopFilterWithSidebar && isDesktop && totalProducts ? (
              <FilterWithSidebarItemCountAndSort
                noOfItems={totalProducts}
                sortBy=""
                updateQuery={updateQuery}
                skipSorting
              />
            ) : null}
            <SearchResults
              searchQuery={searchQuery}
              productCards={productCards}
              isLayoutLoading={isLayoutLoading}
              intersectRef={intersectRef}
              isMoreProductsLoading={isMoreProductsLoading}
              totalProducts={totalProducts}
              filters={filters}
              onResetFilters={handleResetFilters}
              isNewCLPFiltersExperimentActive={isNewCLPFiltersExperimentActive}
              showDesktopFilterWithSidebar={showDesktopFilterWithSidebar}
            />
          </div>
        </div>
      </LoaderWrapper>
    </div>
  )
}

function withSearchQueryRemount(Component: FC<PropsWithChildren>) {
  return function WithSearchQueryRemount(props: PropsWithChildren) {
    const router = useQuinceRouter()
    const searchQuery = router.query.q as string

    return <Component key={searchQuery} {...props} />
  }
}

export default withSearchQueryRemount(SearchResultsSection)
